// CR159+CR226: Variants indicator badge with popover showing variant purchase brands
// CR226: Added evidenceJson fallback support
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getVariantsWithFallback, type ExtractedDeal } from "@/lib/batch-runs/validatedPayloadUtils";
import { Layers } from "lucide-react";

interface Variant {
  variant_type?: string;
  purchase_brand?: string;
  brand_source?: string;
  brand_evidence?: string[];
}

interface VariantsBadgeProps {
  validatedPayloadJson: unknown;
  /** CR226: Optional evidence_json for fallback */
  evidenceJson?: unknown;
  /** Use popover for more detail instead of tooltip */
  usePopover?: boolean;
}

/**
 * CR159+CR226: Displays variant count badge with quick preview list
 * - Shows "Variants: N" pill when deal has variants
 * - Tooltip/popover shows up to 6 variant purchase brands
 * - If > 6, shows "+N more"
 * - CR226: Falls back to evidence_json.raw_deal.variants if validated_payload_json lacks variants
 */
export function VariantsBadge({ validatedPayloadJson, evidenceJson, usePopover = false }: VariantsBadgeProps) {
  // CR226: Use fallback helper with evidence_json support
  const variants = getVariantsWithFallback(validatedPayloadJson, evidenceJson) as Variant[];
  
  if (!variants || variants.length === 0) {
    return null;
  }
  
  const count = variants.length;
  const maxPreview = 6;
  const previewVariants = variants.slice(0, maxPreview);
  const remaining = count - maxPreview;
  
  // Extract purchase brand labels for preview
  const brandLabels = previewVariants.map((v, i) => {
    if (v.purchase_brand) return v.purchase_brand;
    if (v.variant_type) return v.variant_type;
    // Fallback: show trimmed JSON for debug
    return `Variant ${i + 1}`;
  });
  
  const previewText = brandLabels.join(", ") + (remaining > 0 ? `, +${remaining} more` : "");
  
  const badgeContent = (
    <Badge 
      variant="outline" 
      className="gap-1 bg-blue-50 text-blue-700 border-blue-200 cursor-pointer"
    >
      <Layers className="h-3 w-3" />
      Variants: {count}
    </Badge>
  );
  
  if (usePopover) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          {badgeContent}
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Layers className="h-4 w-4" />
              {count} Variant{count > 1 ? "s" : ""} (Purchase Brands)
            </div>
            <div className="flex flex-wrap gap-1">
              {variants.map((v, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  {v.purchase_brand || v.variant_type || `Variant ${i + 1}`}
                </Badge>
              ))}
            </div>
            {variants.some(v => v.brand_source) && (
              <div className="text-xs text-muted-foreground mt-2">
                <span className="font-medium">Sources:</span>{" "}
                {Array.from(new Set(variants.map(v => v.brand_source).filter(Boolean))).join(", ")}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeContent}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-xs">{previewText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * CR159+CR226: Full variants list for detail panels
 * Shows all variants with complete metadata
 */
export function VariantsDetailList({ validatedPayloadJson, evidenceJson }: { validatedPayloadJson: unknown; evidenceJson?: unknown }) {
  // CR226: Use fallback helper with evidence_json support
  const variants = getVariantsWithFallback(validatedPayloadJson, evidenceJson) as Variant[];
  
  if (!variants || variants.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-2">
      <h4 className="font-medium flex items-center gap-2">
        <Layers className="h-4 w-4" />
        Variants ({variants.length})
      </h4>
      <div className="grid gap-2">
        {variants.map((v, i) => (
          <div 
            key={i} 
            className="p-2 border rounded-md bg-muted/30 text-sm"
          >
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {v.variant_type || "PURCHASE_BRAND"}
              </Badge>
              <span className="font-medium">
                {v.purchase_brand || `Variant ${i + 1}`}
              </span>
            </div>
            {(v.brand_source || (v.brand_evidence && v.brand_evidence.length > 0)) && (
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                {v.brand_source && (
                  <div><span className="font-medium">Source:</span> {v.brand_source}</div>
                )}
                {v.brand_evidence && v.brand_evidence.length > 0 && (
                  <div>
                    <span className="font-medium">Evidence:</span>{" "}
                    {v.brand_evidence.slice(0, 3).join(", ")}
                    {v.brand_evidence.length > 3 && ` +${v.brand_evidence.length - 3} more`}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
